<template>
  <q-card flat class="q-pa-sm">
    <div class="text-h6" :class="$q.dark.mode ? 'text-grey-2' : 'text-primary'">
      Fundamental Scan Pro
    </div>

    <PackageAuth>
      <template #authorized-content>
        <FundamentalScan
          :isChart="true"
          :widgetWidth="widgetWidth"
          @emitData="
            (eventData) => {
              fundamentalScanData = eventData;
            }
          "
        />

        <div class="q-py-sm">
          <q-table
            flat
            bordered
            :rows="fundamentalScanData"
            :columns="columns"
            :pagination="pagination"
            binary-state-sort
            separator="cell"
            :filter="filterSymbol"
            :filter-method="filterData"
            :class="
              $q.dark.mode === true
                ? 'table-sticky-header-column-dark'
                : 'table-sticky-header-column'
            "
          >
            <template v-slot:top-left>
              <q-input
                bordered
                dense
                debounce="300"
                v-model="filterSymbol"
                placeholder="Filter Symbols"
                class="q-mr-md ellipsis"
                style="width: 150px"
              />
            </template>

            <template v-slot:body-cell-symbol="props">
              <q-td :props="props">
                <q-item dense clickable @click="onClickSymbolEmit(props.value)">
                  <div class="col">
                    {{ props.value }}
                    <q-tooltip>{{
                      allStocks.find((stock) => stock.symbol == props.value)
                        ?.name
                    }}</q-tooltip>
                  </div>
                </q-item>
              </q-td>
            </template>
          </q-table>
        </div>
      </template>
    </PackageAuth>
  </q-card>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useQuasar } from "quasar";
import { useStore } from "vuex";
import useUtils from "src/use/utils";
import FundamentalScan from "src/components/FundamentalScanPro/FundamentalScanProComponents.vue";
import PackageAuth from "src/components/Packages/PackageAuth.vue";

const $q = useQuasar();
const $store = useStore();
const { amountConversion } = useUtils();

const props = defineProps({
  widgetWidth: {
    type: String,
  },
});

const emit = defineEmits(["chartSymbol"]);

const allStocks = ref([]);
const fundamentalScanData = ref([]);

const columns = [
  {
    name: "symbol",
    label: "Symbol",
    field: "symbol",
    align: "left",
    sortable: true,
    style: "max-width: 120px",
    classes: "ellipsis",
  },
  {
    name: "fiscal_year",
    label: "Fiscal Year / Quarter",
    field: "fiscal_year",
    align: "center",
    sortable: true,
    format: (val, row) => {
      return val ? `${row.fiscal_year} / ${row.quarter}` : "-";
    },
    style: "max-width: 120px",
    classes: "ellipsis",
  },
  {
    name: "expired_date",
    label: "Expired Date",
    field: "expired_date",
    align: "center",
    sortable: true,
    format: (val, row) => {
      return val ? val : "-";
    },
    style: "max-width: 120px",
    classes: "ellipsis",
  },
  {
    name: "close",
    label: "Price",
    field: "close",
    align: "center",
    sortable: true,
    format: (val, row) => {
      return val ? parseFloat(val).toFixed(2) : "-";
    },
    style: "max-width: 120px",
    classes: "ellipsis",
  },
  {
    name: "eps",
    label: "EPS",
    field: "eps",
    align: "center",
    sortable: true,
    format: (val, row) => {
      return val ? parseFloat(val).toFixed(2) : "-";
    },
    style: "max-width: 120px",
    classes: "ellipsis",
  },
  {
    name: "net_worth",
    label: "Net Worth",
    field: "net_worth",
    sortable: true,
    format: (val, row) => {
      return val ? parseFloat(val).toFixed(2) : "-";
    },
    align: "center",
    style: "max-width: 120px",
    classes: "ellipsis",
  },
  // {
  //   name: "peg_ratio",
  //   label: "PEG Ratio",
  //   field: "peg_ratio",
  //   format: (val, row) => {
  //     return val ? parseFloat(val).toFixed(2) : "-";
  //   },
  //   align: "center",
  //   sortable: true,
  //   style: "max-width: 120px",
  //   classes: "ellipsis",
  // },
  {
    name: "pe_ratio",
    label: "PE Ratio",
    field: "pe_ratio",
    format: (val, row) => {
      return val ? parseFloat(val).toFixed(2) : "-";
    },
    align: "center",
    sortable: true,
    style: "max-width: 120px",
    classes: "ellipsis",
  },
  {
    name: "pb_ratiot_worth",
    label: "PB Ratio",
    field: "pb_ratiot_worth",
    format: (val, row) => {
      return val ? parseFloat(val).toFixed(2) : "-";
    },
    align: "center",
    sortable: true,
    style: "max-width: 120px",
    classes: "ellipsis",
  },
  {
    name: "npl",
    label: "NPL",
    field: "npl",
    format: (val, row) => {
      return val ? parseFloat(val).toFixed(2) : "-";
    },
    align: "center",
    sortable: true,
    style: "max-width: 120px",
    classes: "ellipsis",
  },
  {
    name: "listed_share",
    label: "Listed Share",
    field: "listed_share",
    format: (val, row) => {
      // return val ? parseFloat(val / 100000).toFixed(2) : "-";
      return val ? amountConversion(val) : "-";
    },
    align: "center",
    sortable: true,
    style: "max-width: 120px",
    classes: "ellipsis",
  },
  {
    name: "floated_share",
    label: "Floated Share",
    field: "floated_share",
    format: (val, row) => {
      // return val ? parseFloat(val / 100000).toFixed(2) : "-";
      return val ? amountConversion(val) : "-";
    },
    align: "center",
    sortable: true,
    style: "max-width: 120px",
    classes: "ellipsis",
  },
];

const pagination = ref({
  rowsPerPage: 50,
});

const filterSymbol = ref("");
const filterData = (rows, terms, cols, getCellValue) => {
  return rows.filter((row) => {
    return row.symbol.toLowerCase().includes(filterSymbol.value.toLowerCase());
  });
};

onMounted(async () => {
  if (!allStocks.value?.length) {
    await $store.dispatch("core/getAllStocks");
    allStocks.value = $store.state.core.allStocks;
  }
});

const onClickSymbolEmit = (symbol) => {
  emit("chartSymbol", symbol);
};
</script>
